import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";
import { MapPin, ArrowUpRight } from "lucide-react";

const PROJECT_IMAGES = [
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };
const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

// Bento spans (12-col)
const SPANS = [
  "lg:col-span-7 lg:row-span-2",
  "lg:col-span-5",
  "lg:col-span-5",
  "lg:col-span-7",
];

export function Projects() {
  const { t, dict } = useI18n();
  const items = dict.projects.items;

  const openContact = (e) => {
    e.preventDefault();
    const el = document.querySelector("#contacto");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="proyectos" className="relative py-20 lg:py-32" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-emerald-500" />
              <p className="eyebrow">{t("projects.eyebrow")}</p>
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] text-balance">
              {t("projects.heading")}
            </h2>
            <p className="mt-5 text-base text-slate-400 leading-relaxed">{t("projects.subtitle")}</p>
          </div>
        </div>

        <motion.div
          className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 lg:auto-rows-[240px]"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          variants={container}
        >
          {items.map((p, i) => (
            <motion.article
              key={p.title}
              variants={item}
              className={`z-card group relative overflow-hidden ${SPANS[i]}`}
              data-testid={`project-card-${i + 1}`}
            >
              <div className="absolute inset-0">
                <img
                  src={PROJECT_IMAGES[i]}
                  alt={p.alt}
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/40 to-transparent" />
              </div>

              <div className="relative z-10 h-full min-h-[240px] flex flex-col justify-between p-6 lg:p-7">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-emerald-300">
                    {p.tag}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-700 bg-[#0B132B]/70 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-slate-300">
                    {p.meta}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-2xl lg:text-3xl font-bold text-white leading-tight">
                    {p.title}
                  </h3>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-300">
                      <MapPin className="w-3.5 h-3.5 text-emerald-400" /> {p.city}
                    </p>
                    <a
                      href="#contacto"
                      onClick={openContact}
                      className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-emerald-300 hover:text-white transition-colors"
                      data-testid={`project-cta-${i + 1}`}
                    >
                      {t("projects.cta")} <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default Projects;
